import { Capacitor } from '@capacitor/core';
import { licenseStateOf, normalizeLicenseNumber, type AccountDevice, type AccountDeviceInfo, type BindOutcome, type BoundLicense, type DevicePlatform, type DeviceState, type LicenseState } from '../../supabase/functions/_shared/accountProtocol';
import { currentAccessToken } from '../backend/aiClient';
import { getAccountTransport, type AccountFailureCode } from '../backend/accountClient';
import { getSupabase } from '../backend/supabaseAdapter';
import { V2_DEVICE_ID_KEY } from '../persistence/persistenceContract';
import { cachedBoundLicense, forgetBoundLicenseMemory, writeBoundLicense } from './boundLicense';
import { createStore } from './createStore';

// The account as the server knows it (Owner decision list 2026-10-19 item 50; the rules are
// supabase/functions/_shared/accountProtocol.ts L-1 … L-7):
//   - the inspector's New York licence, bound once after New York's list confirmed it — the ONLY source
//     of the licence and the report name on this device (src/state/preferences.ts reads it from here);
//   - this device: allowed, waiting for the e-mail code, or removed because a newer device signed in.
// The last answer is cached per account, so field work continues offline with the licence it had.
export type AccountServerState = 'unknown' | 'ok' | 'offline' | 'unavailable' | 'notConfigured' | 'unauthenticated';

export interface AccountState {
  license: BoundLicense | null;
  device: DeviceState | 'unknown';
  devices: AccountDevice[];
  serverState: AccountServerState;
  lastSyncedAt: number | null;
  busy: 'refresh' | 'bind' | 'code' | null;
}

function emptyState(): AccountState {
  // The licence is NOT read here: this runs while an account is being left / entered, before the new
  // account's storage scope exists. currentLicense() reads the cache when it is asked.
  return { license: null, device: 'unknown', devices: [], serverState: 'unknown', lastSyncedAt: null, busy: null };
}

export const accountStore = createStore<AccountState>({ license: null, device: 'unknown', devices: [], serverState: 'unknown', lastSyncedAt: null, busy: null });

let clock: () => number = () => Date.now();

export function setAccountClockForTests(now: (() => number) | null): void {
  clock = now ?? (() => Date.now());
}

// ---------- this installation ----------

function randomId(): string {
  const bytes = new Uint8Array(12);
  globalThis.crypto.getRandomValues(bytes);
  return Array.from(bytes, (byte) => byte.toString(16).padStart(2, '0')).join('');
}

// One random id per installation (not per account): what the server counts as "a device" (L-5).
export function deviceId(): string {
  try {
    const known = window.localStorage.getItem(V2_DEVICE_ID_KEY);
    if (known && /^[A-Za-z0-9_-]{8,80}$/.test(known)) return known;
    const fresh = `d${randomId()}`;
    window.localStorage.setItem(V2_DEVICE_ID_KEY, fresh);
    return fresh;
  } catch {
    return 'd-unstored-device';
  }
}

export function devicePlatform(): DevicePlatform {
  const platform = Capacitor.getPlatform();
  return platform === 'ios' ? 'ios' : platform === 'android' ? 'android' : 'web';
}

function deviceLabel(platform: DevicePlatform): string {
  if (platform === 'ios') return /iPad/i.test(navigator.userAgent) ? 'iPad' : 'iPhone';
  if (platform === 'android') return 'Android';
  const agent = typeof navigator === 'undefined' ? '' : navigator.userAgent;
  const browser = /Edg\//.test(agent) ? 'Edge' : /Chrome\//.test(agent) ? 'Chrome' : /Safari\//.test(agent) ? 'Safari' : /Firefox\//.test(agent) ? 'Firefox' : 'Web';
  const system = /Windows/.test(agent) ? 'Windows' : /Mac OS X|Macintosh/.test(agent) ? 'Mac' : '';
  return [browser, system].filter((part) => part.length > 0).join(' · ');
}

export function thisDevice(): AccountDeviceInfo {
  const platform = devicePlatform();
  return { deviceId: deviceId(), platform, label: deviceLabel(platform) };
}

// ---------- the per-account cache (src/state/boundLicense.ts) ----------

// The licence state right now (L-6), from the server's last answer or the cache.
export function currentLicense(): BoundLicense | null {
  return accountStore.get().license ?? cachedBoundLicense();
}

export function currentLicenseState(at: number = clock()): LicenseState {
  return licenseStateOf(currentLicense(), at);
}

// ---------- talking to the server ----------

function serverStateFor(code: AccountFailureCode): AccountServerState {
  if (code === 'offline' || code === 'network' || code === 'timeout') return 'offline';
  if (code === 'notConfigured') return 'notConfigured';
  if (code === 'unauthenticated') return 'unauthenticated';
  return 'unavailable';
}

function apply(license: BoundLicense | null, device: DeviceState, devices: AccountDevice[]): void {
  const at = clock();
  writeBoundLicense(license, at);
  accountStore.set((prev) => ({ ...prev, license, device, devices, serverState: 'ok', lastSyncedAt: at }));
  if (device === 'revoked') void onDeviceRevoked();
}

// The removed device signs out (L-5); the sign-in screen says why.
let revokedHandler: (() => Promise<void>) | null = null;

export function registerDeviceRevokedHandler(handler: (() => Promise<void>) | null): void {
  revokedHandler = handler;
}

async function onDeviceRevoked(): Promise<void> {
  markSignOutReason('deviceRevoked');
  await revokedHandler?.();
}

let refreshing: Promise<void> | null = null;

export async function refreshAccount(): Promise<void> {
  if (refreshing) return refreshing;
  const mine = (async () => {
    accountStore.set((prev) => ({ ...prev, busy: prev.busy ?? 'refresh' }));
    try {
      const token = await currentAccessToken();
      const outcome = await getAccountTransport().status(token, thisDevice());
      if (outcome.kind === 'ok') apply(outcome.response.status.license, outcome.response.status.device, outcome.response.status.devices);
      else accountStore.set((prev) => ({ ...prev, serverState: serverStateFor(outcome.code) }));
    } finally {
      accountStore.set((prev) => ({ ...prev, busy: prev.busy === 'refresh' ? null : prev.busy }));
    }
  })();
  refreshing = mine;
  try {
    await mine;
  } finally {
    if (refreshing === mine) refreshing = null;
  }
}

export type BindResult = { kind: 'done'; outcome: BindOutcome } | { kind: 'failed'; reason: 'offline' | 'unavailable' | 'deviceNotVerified' };

// L-1 … L-3: the server checks the number and the name against New York's list and binds once.
export async function bindLicenseAction(input: { licenseNumber: string; firstName: string; lastName: string }): Promise<BindResult> {
  accountStore.set((prev) => ({ ...prev, busy: 'bind' }));
  try {
    const token = await currentAccessToken();
    const outcome = await getAccountTransport().bindLicense(token, thisDevice(), { licenseNumber: normalizeLicenseNumber(input.licenseNumber) ?? input.licenseNumber.trim(), firstName: input.firstName.trim(), lastName: input.lastName.trim() });
    if (outcome.kind !== 'ok') {
      accountStore.set((prev) => ({ ...prev, serverState: serverStateFor(outcome.code) }));
      return { kind: 'failed', reason: serverStateFor(outcome.code) === 'offline' ? 'offline' : 'unavailable' };
    }
    const status = outcome.response.status;
    apply(status.license, status.device, status.devices);
    if (status.device !== 'active') return { kind: 'failed', reason: 'deviceNotVerified' };
    return { kind: 'done', outcome: outcome.response.bind ?? 'listUnavailable' };
  } finally {
    accountStore.set((prev) => ({ ...prev, busy: null }));
  }
}

// ---------- the e-mail code for a new device (L-7) ----------
//
// Supabase sends the account a one-time code (signInWithOtp, never creating an account) and verifying
// it starts a new session whose token records the code — the proof the server checks.
export interface EmailCodeAdapter {
  send(email: string): Promise<'sent' | 'rateLimited' | 'failed'>;
  verify(email: string, code: string): Promise<'ok' | 'invalid' | 'failed'>;
}

const supabaseEmailCodes: EmailCodeAdapter = {
  async send(email) {
    const client = getSupabase();
    if (!client) return 'failed';
    const { error } = await client.auth.signInWithOtp({ email: email.trim(), options: { shouldCreateUser: false } });
    if (!error) return 'sent';
    return /rate limit|too many|seconds/i.test(error.message ?? '') ? 'rateLimited' : 'failed';
  },
  async verify(email, code) {
    const client = getSupabase();
    if (!client) return 'failed';
    const { error } = await client.auth.verifyOtp({ email: email.trim(), token: code.trim(), type: 'email' });
    if (!error) return 'ok';
    return /expired|invalid|token/i.test(error.message ?? '') ? 'invalid' : 'failed';
  }
};

let emailCodes: EmailCodeAdapter = supabaseEmailCodes;

export function setEmailCodeAdapterForTests(adapter: EmailCodeAdapter | null): void {
  emailCodes = adapter ?? supabaseEmailCodes;
}

export async function sendDeviceCode(email: string): Promise<'sent' | 'rateLimited' | 'failed'> {
  accountStore.set((prev) => ({ ...prev, busy: 'code' }));
  try {
    return await emailCodes.send(email);
  } finally {
    accountStore.set((prev) => ({ ...prev, busy: null }));
  }
}

export async function verifyDeviceCode(email: string, code: string): Promise<'ok' | 'invalid' | 'failed' | 'stillRequired'> {
  accountStore.set((prev) => ({ ...prev, busy: 'code' }));
  try {
    const verified = await emailCodes.verify(email, code);
    if (verified !== 'ok') return verified;
  } finally {
    accountStore.set((prev) => ({ ...prev, busy: null }));
  }
  await refreshAccount();
  return accountStore.get().device === 'active' ? 'ok' : 'stillRequired';
}

// ---------- why the last sign-out happened (shown once on the sign-in screen) ----------

export type SignOutReason = 'deviceRevoked';

let signOutReason: SignOutReason | null = null;

export function markSignOutReason(reason: SignOutReason | null): void {
  signOutReason = reason;
}

export function takeSignOutReason(): SignOutReason | null {
  const reason = signOutReason;
  signOutReason = null;
  return reason;
}

// Entering / leaving an account: start from that account's cache, never the previous one's.
export function resetAccountStore(): void {
  forgetBoundLicenseMemory();
  accountStore.set(() => emptyState());
}

export function resetAccountStoreForTests(): void {
  forgetBoundLicenseMemory();
  refreshing = null;
  signOutReason = null;
  accountStore.set(() => ({ license: null, device: 'unknown', devices: [], serverState: 'unknown', lastSyncedAt: null, busy: null }));
}
